// src/router/guards.js
import { Capacitor } from '@capacitor/core';
import store from '@/store';
import { cookieManager } from '@/service/cookieManager';
import { changeLocale, i18n } from '@/i18n';
import { faviconService } from '@/service/faviconService';
import { deviceManager } from '@/service/deviceManager';
import { copilotRepository } from '@/service/repository/copilotRepository';
import { configRepository } from '@/service/repository/configRepository';
import BackendSyncService from '@/service/backendSyncService';
import { SYNC_PROFILES } from '@/service/backendSyncProfiles';
import { resolveSyncTransport } from '@/service/resolveSyncTransport';
import { syncServerTime } from '@/utils/serverTimeSync';
import { venueRoomLabel } from '@/utils/venueRoomLabel';

const loadUser = async (uid = null, app = null) => {
	if (uid) {
		await store.dispatch('user/find', uid);
		return { user: store.getters['user/user'], hadToken: false };
	}

	const hadToken = !!cookieManager.getToken();
	if (hadToken) {
		// Always hit check-access on route entry; backend issues a fresh JWT (rolling 30-day TTL).
		const deviceToken = app === 'copilot' ? deviceManager.getOrCreateToken() : null;
		await store.dispatch('user/loginWithToken', { token: cookieManager.getToken(), app, deviceToken });
	}
	return { user: store.getters['user/user'], hadToken };
};

const redirectToLogin = (next, hadToken) => {
	if (hadToken) {
		next({ path: '/login', query: { reason: 'session_expired' } });
	} else {
		next('/login');
	}
};

const initializeAppPublic = async () => {
	await store.dispatch('config/showLoadingScreen');
	const websocketUrl = store.getters['config/websocketUrl'];
	const venueId = store.getters['venue/venueId'];

	if (!websocketUrl || !venueId) {
		const appConfig = await configRepository.getAppConfig();

		if (appConfig) {
			syncServerTime(appConfig.serverDateTime);

			store.commit('config/initConfig', appConfig.systemConfig);
			store.commit('config/initLogicalDate', appConfig.logicalDate);
			store.commit('venue/initConfigVenue', appConfig.venueConfig);

			store.commit('copilot/setServerLogicalDate', appConfig.logicalDate);
			store.commit('copilot/setSelectedDate', { selectedDate: appConfig.logicalDate });
		} else {
			// Fallback to individual calls if bundled endpoint fails
			await store.dispatch('config/loadSystemConfig');
			await store.dispatch('venue/loadVenueConfig');

			const logicalDate = store.getters['config/logicalDate'];
			store.commit('copilot/setServerLogicalDate', logicalDate);
			store.commit('copilot/setSelectedDate', { selectedDate: logicalDate });
		}
	}

	const targetLocale = cookieManager.getCookie('selectedLocale') || 'es';
	await changeLocale(i18n, targetLocale);
};

const initializeCopilotAfterAuth = async () => {
	await store.dispatch('copilot/fetchStaffList');

	// Fire-and-forget: register/update this device on the backend.
	// Non-blocking — the app never waits for this response.
	copilotRepository.registerDevice(deviceManager.getDeviceInfo()).catch(() => {});
};

/**
 * Loads all copilot static data from the bundled /copilot/init endpoint,
 * then hydrates the relevant stores. Falls back to individual calls on failure.
 */
const loadCopilotInitData = async (user) => {
	const initData = await copilotRepository.getCopilotInit();

	if (initData) {
		// Hydrate staff
		const staffList = Array.isArray(initData.staff) ? initData.staff : [];
		store.commit('copilot/setStaffList', staffList);

		// Hydrate copilot roles
		if (user && user.copilotRoles && user.copilotRoles.length > 0) {
			store.commit('copilot/setAvailableRoles', { availableRoles: user.copilotRoles, isVenueMaster: true });
			user.copilotRoles.forEach((role) => {
				if (role.active === 1) {
					store.commit('copilot/setSelectedRoleId', { selectedRoleId: role.id });
				}
			});
		} else if (initData.copilotRoles) {
			store.commit('copilot/setAvailableRoles', { availableRoles: initData.copilotRoles, isVenueMaster: true });
		}

		// Hydrate tables — prefer top-level `rooms` (new); fall back to `tables.rooms` (legacy)
		const rawRooms = initData.rooms ?? initData.tables?.rooms;
		if (rawRooms) {
			let rooms = Array.isArray(rawRooms) ? rawRooms : Object.values(rawRooms);
			const tables = rooms.flatMap((room) =>
				room.tables.map((table) => ({
					...table,
					room: room.name,
					roomLabel: venueRoomLabel(room),
					roomServiceMode: room.serviceMode ?? null,
				})),
			);
			store.commit('copilot/setTables', { tables });
		}

		// Hydrate accounting config
		if (initData.accountingConfig) {
			store.commit('accounting/initConfigAccounting', initData.accountingConfig);
		}

		// Hydrate service ranges
		if (initData.serviceRanges) {
			store.commit('copilot/setServiceRanges', initData.serviceRanges);
		}

		// Fire-and-forget device registration
		copilotRepository.registerDevice(deviceManager.getDeviceInfo()).catch(() => {});

		return true;
	}

	// Fallback: use individual endpoints
	await initializeCopilotAfterAuth();
	await store.dispatch('copilot/initAvailableRoles', { user });

	const venueId = store.getters['venue/venueId'];
	await Promise.all([
		store.dispatch('accounting/loadAccountingConfig'),
		store.dispatch('copilot/getTables'),
		venueId ? store.dispatch('copilot/getServiceRanges', { venueId, force: true }) : Promise.resolve(),
	]);

	return false;
};

export function setupGuards(router) {
	router.beforeEach(async (to, from, next) => {
		let user = null;

		await initializeAppPublic();

		if (to.name !== 'Menu' && to.name !== 'Reserve' && to.name !== 'Home') {
			cookieManager.setConsent(true);
		}

		const uid = to.query.uid;

		faviconService.changeFaviconByRoute(to.path);
		document.title = `${to.meta.title} | ${store.getters['venue/businessName']}` || 'WeWelcom';

		// Event-venue check: always run so isEventVenue is available; event venues only use /eventos
		const eventVenueSubdomains = ['macarfi'];
		const eventVenueResourceIds = ['macarfi'];
		let venueSubdomain = store.state.venue.subdomain;
		let venueResourceId = store.getters['venue/resourceId'];
		if (venueSubdomain == null && venueResourceId == null) {
			await store.dispatch('venue/loadVenueConfig');
			venueSubdomain = store.state.venue.subdomain;
			venueResourceId = store.getters['venue/resourceId'];
		}
		const isEventVenue =
			eventVenueSubdomains.includes(venueSubdomain) || eventVenueResourceIds.includes(venueResourceId);
		switch (to.name) {
			case 'Root': {
				if (Capacitor.isNativePlatform()) {
					next('/copilot');
				} else if (isEventVenue) {
					next('/eventos');
				} else {
					next('/reservar');
				}
				return;
			}

			case 'Menu': {
				const menuAuth = await loadUser(uid);
				user = menuAuth.user;
				await store.dispatch('menu/loadMenus');
				break;
			}

			case 'TakeAway': {
				const takeAwayAuth = await loadUser(uid);
				user = takeAwayAuth.user;
				await store.dispatch('menu/loadMenus');
				break;
			}

			case 'Reserve':
				store.commit('reservationForm/setIsIframe', { isIframe: false });
				await store.dispatch('reservationForm/setAvailableRooms');
				break;

			case 'IframeReserve':
				store.commit('reservationForm/setIsIframe', { isIframe: true });
				await store.dispatch('reservationForm/setAvailableRooms');
				break;

			case 'Copilot': {
				if (isEventVenue) {
					next('/eventos');
					return;
				}

				const copilotAuth = await loadUser(uid, 'copilot');
				user = copilotAuth.user;
				if (!user) {
					redirectToLogin(next, copilotAuth.hadToken);
					return;
				}

				// Start WebSocket connection early (fire-and-forget)
				BackendSyncService.connect({
					profile: SYNC_PROFILES.COPILOT,
					transport: resolveSyncTransport(),
					context: {
						venueId: store.getters['venue/venueId'],
						deviceToken: deviceManager.getOrCreateToken(),
					},
				}).catch(() => {});

			// Load cached static data first (single fast request, avoids CPU burst)
			await loadCopilotInitData(user);

			// Apply saved role selection AFTER roles are loaded
			const selectedRoleId = cookieManager.getCookie('selectedRole');
			store.commit('copilot/setSelectedRoleId', { selectedRoleId });

			// Dynamic data loads in the background — does NOT block showing the PIN screen
			Promise.all([
				store.dispatch('menu/loadMenus'),
				store.dispatch('copilot/updateReservations'),
				store.dispatch('copilot/updateOrdersAndNotifications'),
			]).catch(() => {});

				break;
			}

			case 'Ebitdata': {
				await store.dispatch('ebitdata/getTables');
				const ebitdataAuth = await loadUser(uid, 'admin');
				user = ebitdataAuth.user;
				if (!user) {
					redirectToLogin(next, ebitdataAuth.hadToken);
					return;
				}
				break;
			}

			case 'Kiosk': {
				const kioskAuth = await loadUser(uid, 'wehost');
				user = kioskAuth.user;
				if (!user) {
					redirectToLogin(next, kioskAuth.hadToken);
					return;
				}
				await initializeCopilotAfterAuth();
				break;
			}

			case 'WeHost': {
				const weHostAuth = await loadUser(uid, 'wehost');
				user = weHostAuth.user;
				if (!user) {
					redirectToLogin(next, weHostAuth.hadToken);
					return;
				}
				break;
			}

			case 'Events': {
				const eventsAuth = await loadUser(uid, 'copilot');
				user = eventsAuth.user;
				if (!user) {
					redirectToLogin(next, eventsAuth.hadToken);
					return;
				}
				await initializeCopilotAfterAuth();
				break;
			}

			case 'Home':
				if (store.getters['venue/resourceId'] === 'elcapirotedegranada') {
					faviconService.setFavicon(
						`${store.getters['config/weWelcomUrl']}/world/img/${store.getters['venue/resourceId']}/favicon/favicon.ico`,
					);
				}
				await store.dispatch('reservationForm/setAvailableRooms');
				break;
		}

		next();
	});

	router.afterEach((to) => {
		const body = document.body;
		body.classList.remove('copilot', 'kiosk', 'login');

		if (to.path.startsWith('/copilot')) {
			body.classList.add('copilot');
		}
		if (to.path.startsWith('/kiosk')) {
			body.classList.add('kiosk');
		}
		if (to.path.startsWith('/login')) {
			body.classList.add('login');
		}

		store.dispatch('config/hideLoadingScreen');
	});
}
